// Partner day view — read-only dashboard scoped by the partner's privacy
// tier toward me.
//
// Tier behavior:
//   vitals            → calorie ring + 4 macro bars (totals only)
//   vitals_nutrients  → above + 25-nutrient panel
//   plus_meal_names   → above + meal names (no portions / no photos)
//   full              → above + portions, items, edits visible
//
// We use partner_day_summary() RPC for the aggregate-only tiers (so
// individual meals never leave the DB). For plus_meal_names + full, we
// query meals directly (RLS gates the row read at the DB layer).

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Lock } from "lucide-react";
import BackButton from "../components/BackButton";
import UpgradePrompt from "../components/UpgradePrompt";
import { useIsPro } from "../lib/subscription";
import { supabase } from "../lib/supabase";
import {
  loadConnections,
  loadPartnerDaySummary,
  PRIVACY_LABELS,
  type Connection,
  type PartnerDaySummary,
  type PrivacyTier
} from "../lib/partners";
import { friendlyError } from "../lib/errors";
import DateNav from "../components/DateNav";
import { localDateOf, todayISO } from "../lib/storage";
import type { LoggedMeal, MealEstimate } from "../lib/types";
import { sumDayNutrients, RDA, type NutrientPanel } from "../lib/nutrients";
import { DEFAULT_GOALS, type Goals } from "../lib/goals";

export default function PartnerDay() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const isPro = useIsPro();

  const [connection, setConnection] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(todayISO());

  // Per-tier data containers — pulled depending on the partner's tier
  const [summary, setSummary] = useState<PartnerDaySummary | null>(null);
  const [meals, setMeals] = useState<LoggedMeal[] | null>(null);
  const [partnerGoals, setPartnerGoals] = useState<Goals | null>(null);

  const tier: PrivacyTier | null = connection?.theyShare ?? null;
  const showMealList = tier === "plus_meal_names" || tier === "full";
  const showMealDetails = tier === "full";

  // Load connection + day data on mount / when day changes
  const refresh = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Resolve the connection so we know the tier
      const all = await loadConnections();
      const found = all.find((c) => c.partnerId === partnerId && c.status === "accepted");
      if (!found) {
        setError("That partner isn't connected to you.");
        setLoading(false);
        return;
      }
      setConnection(found);

      // 2. Pull day summary + partner goals in parallel. Goals show the
      // ring target — visible at every tier (just calorie/macro numbers,
      // not sensitive). Falls back to DEFAULT_GOALS if RLS denies or the
      // partner hasn't customized theirs.
      const [sum, goals] = await Promise.all([
        loadPartnerDaySummary(partnerId, selectedDate),
        loadPartnerGoals(partnerId)
      ]);
      setSummary(sum);
      setPartnerGoals(goals);

      // 3. For meal-visible tiers, pull individual meals via RLS-gated read
      if (found.theyShare === "plus_meal_names" || found.theyShare === "full") {
        const list = await loadPartnerMeals(partnerId, selectedDate);
        setMeals(list);
      } else {
        setMeals(null);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [partnerId, selectedDate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!partnerId) return null;

  // Free users get a paywall on partner day views — viewing someone else's
  // macros is the load-bearing Pro feature. Sharing your own data + receiving
  // shares stays Free.
  if (!isPro) {
    return (
      <div className="p-4 space-y-6 pb-24">
        <header className="flex items-center gap-3">
          <BackButton onClick={() => navigate("/partners")} label="Partners" />
        </header>
        <UpgradePrompt
          reason="Seeing your partner's day, macros, and nutrients is a Pro feature. Sharing your own data and receiving shared meals stays free."
          features={[
            "Calorie ring + macro bars for every connected partner",
            "Full 25-nutrient panel when they share it",
            "Day navigation — see their history too",
            "Unlimited shared-meal receives with full nutrient data"
          ]}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <header className="flex items-center gap-3">
        <BackButton onClick={() => navigate("/partners")} label="Partners" />
      </header>

      {loading && !connection ? (
        <div className="text-center text-slate-500 py-10 text-sm">Loading…</div>
      ) : error ? (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      ) : connection ? (
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-base font-semibold text-slate-300 flex-shrink-0">
              {connection.partnerEmail.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-slate-100 truncate">{connection.partnerEmail}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Sharing: {PRIVACY_LABELS[connection.theyShare]}
              </div>
            </div>
          </div>

          <DateNav
            selectedDate={selectedDate}
            todayDate={todayISO()}
            onChange={setSelectedDate}
          />

          {/* Totals card with ring + goal bars — always shown */}
          <TotalsCard summary={summary} goals={partnerGoals ?? DEFAULT_GOALS} />

          {/* Nutrient panel — vitals_nutrients+ */}
          {summary?.nutrients && <NutrientPanelCard nutrients={summary.nutrients} />}

          {/* Meals list — plus_meal_names + full */}
          {showMealList && meals !== null && (
            <MealsList meals={meals} showDetails={showMealDetails} />
          )}

          {/* Locked-tier hint */}
          {!showMealList && (
            <LockedTierHint tier={connection.theyShare} />
          )}
        </>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function TotalsCard({
  summary,
  goals
}: {
  summary: PartnerDaySummary | null;
  goals: Goals;
}) {
  const eaten = summary?.calories ?? 0;
  const remaining = Math.max(0, goals.calories - eaten);
  // Ring fraction = how much of the goal has been consumed. Cap at 1.0 visually
  // so an over-budget day fills the ring; the "over by" copy below tells the
  // truth.
  const fraction = Math.min(1, eaten / Math.max(goals.calories, 1));
  const circumference = 2 * Math.PI * 36;
  const dashOffset = circumference * (1 - fraction);
  const over = eaten > goals.calories;
  const noMeals = !summary || summary.meal_count === 0;

  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-4">
      {/* Calorie ring */}
      <div className="relative w-24 h-24 flex-shrink-0">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
          <circle cx="40" cy="40" r="36" fill="none" stroke="#1e293b" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke={over ? "#fb7185" : "#34d399"}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xl font-bold text-slate-100 tabular-nums leading-none">
            {Math.round(eaten)}
          </div>
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-0.5">
            of {goals.calories}
          </div>
          <div className={`text-[10px] tabular-nums mt-0.5 ${over ? "text-rose-400" : "text-slate-400"}`}>
            {over ? `+${Math.round(eaten - goals.calories)} over` : `${Math.round(remaining)} left`}
          </div>
        </div>
      </div>

      {/* Macro bars */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <MacroBar label="Protein" eaten={summary?.protein_g ?? 0} goal={goals.protein_g} color="sky" />
        <MacroBar label="Carbs" eaten={summary?.carbs_g ?? 0} goal={goals.carbs_g} color="amber" />
        <MacroBar label="Fat" eaten={summary?.fat_g ?? 0} goal={goals.fat_g} color="violet" />
        {noMeals && (
          <div className="text-[11px] text-slate-500 pt-1">No meals logged on this day.</div>
        )}
      </div>
    </section>
  );
}

function MacroBar({
  label,
  eaten,
  goal,
  color
}: {
  label: string;
  eaten: number;
  goal: number;
  color: "sky" | "amber" | "violet";
}) {
  const pct = Math.min(100, (eaten / Math.max(goal, 1)) * 100);
  const labelClass = {
    sky: "text-sky-400",
    amber: "text-amber-400",
    violet: "text-violet-400"
  }[color];
  const fillClass = {
    sky: "bg-sky-400",
    amber: "bg-amber-400",
    violet: "bg-violet-400"
  }[color];
  return (
    <div className="flex items-baseline gap-2 text-[11px]">
      <span className={`${labelClass} font-medium w-14 flex-shrink-0`}>{label}</span>
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${fillClass} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-slate-300 tabular-nums w-16 text-right">
        {Math.round(eaten)}<span className="text-slate-500"> / {goal}g</span>
      </span>
    </div>
  );
}

function NutrientPanelCard({ nutrients }: { nutrients: Record<string, number> }) {
  // Pick a handful of headline nutrients to show as %RDA preview.
  // Fiber lives on totals (a macro), not on the nutrient panel — skip it here.
  const HEADLINE: Array<{ key: keyof NutrientPanel; label: string }> = [
    { key: "vit_c_mg", label: "Vit C" },
    { key: "vit_d_ug", label: "Vit D" },
    { key: "iron_mg", label: "Iron" },
    { key: "calcium_mg", label: "Calcium" },
    { key: "potassium_mg", label: "Potassium" },
    { key: "magnesium_mg", label: "Magnesium" }
  ];
  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Nutrients</h3>
        <span className="text-[11px] text-slate-500">6 of 25 shown</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {HEADLINE.map(({ key, label }) => {
          const val = nutrients[key as string] ?? 0;
          const rda = RDA[key as keyof typeof RDA];
          const pct = rda ? Math.min(100, Math.round((val / rda) * 100)) : 0;
          return (
            <div key={key as string} className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-400">{label}</span>
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-400 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] text-slate-500 tabular-nums w-10 text-right">
                {pct}%
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MealsList({ meals, showDetails }: { meals: LoggedMeal[]; showDetails: boolean }) {
  if (meals.length === 0) {
    return (
      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-center text-sm text-slate-500">
        No meals logged on this day.
      </section>
    );
  }
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">Meals</h3>
      <ul className="space-y-2">
        {meals.map((m) => (
          <li
            key={m.id}
            className="p-3 rounded-2xl bg-slate-900 border border-slate-800 space-y-1.5"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-slate-100 truncate">
                {nameForMeal(m.estimate)}
              </span>
              <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">
                {Math.round(m.estimate.totals.calories)} kcal
              </span>
            </div>
            {showDetails && (
              <>
                <div className="text-[11px] text-slate-500">
                  {m.estimate.items.map((i) => i.name).join(" · ")}
                </div>
                <div className="text-[11px] text-slate-400">
                  <span className="text-sky-400">Protein</span> {m.estimate.totals.protein_g.toFixed(0)}g
                  <span className="text-slate-500"> · </span>
                  <span className="text-amber-400">Carbs</span> {m.estimate.totals.carbs_g.toFixed(0)}g
                  <span className="text-slate-500"> · </span>
                  <span className="text-violet-400">Fat</span> {m.estimate.totals.fat_g.toFixed(0)}g
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function LockedTierHint({ tier }: { tier: PrivacyTier }) {
  const next =
    tier === "vitals" ? "Vitals + nutrients" : tier === "vitals_nutrients" ? "+ Meal names" : "Full";
  return (
    <div className="mt-4 p-3 rounded-2xl border border-dashed border-slate-700 text-center text-xs text-slate-500 leading-relaxed">
      <Lock className="w-3 h-3 inline mb-0.5 mr-1" />
      They share <span className="text-slate-300">{PRIVACY_LABELS[tier]}</span> with you. Meals + portions hidden. They can opt up to "{next}" or higher anytime in their Partners tab.
    </div>
  );
}

// ----------------------------------------------------------------------------
// Data helpers
// ----------------------------------------------------------------------------

async function loadPartnerMeals(partnerId: string, day: string): Promise<LoggedMeal[]> {
  if (!supabase) return [];
  // RLS at the DB layer gates whether this returns rows — we already
  // confirmed the partner's tier is plus_meal_names+ before calling.
  const { data, error } = await supabase
    .from("meals")
    .select("*")
    .eq("user_id", partnerId)
    .order("logged_at", { ascending: true });
  if (error) throw error;
  const all = (data ?? []) as LoggedMeal[];
  return all.filter((m) => localDateOf(m.logged_at) === day);
}

async function loadPartnerGoals(partnerId: string): Promise<Goals | null> {
  if (!supabase) return null;
  // RLS allows accepted partners to read each other's goals row.
  const { data, error } = await supabase
    .from("goals")
    .select("calories, protein_g, carbs_g, fat_g")
    .eq("user_id", partnerId)
    .maybeSingle();
  if (error) {
    // Non-fatal: render with DEFAULT_GOALS if we can't read partner's targets.
    console.warn("loadPartnerGoals failed:", error.message);
    return null;
  }
  return (data as Goals | null) ?? null;
}

function nameForMeal(estimate: MealEstimate): string {
  if (estimate.items.length === 0) return "Untitled";
  if (estimate.items.length === 1) return estimate.items[0].name;
  return `${estimate.items[0].name} + ${estimate.items.length - 1} more`;
}

// Re-export for clarity — sumDayNutrients is used in NutrientPanelCard for future
// per-meal aggregation if we add it.
void sumDayNutrients;
